import AccountFullDTO from 'features/accounts/dtos/AccountFullDTO';
import ModalFooter from 'features/shared/components/modal/ModalFooter';
import ModalHeader from 'features/shared/components/modal/ModalHeader';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import AccountRoleSelect from 'features/accounts/components/AccountRoleSelect';
import { Nullable } from 'features/shared/types/nullable';
import AccountRoleDTO from 'features/accounts/dtos/AccountRoleDTO';
import AccountRestrictionDTO from 'features/accounts/dtos/AccountRestrictionDTO';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import AccountRestrictionsSubForm from './AccountRestrictionsSubForm';
import { insertAccountValidations } from './insertAccountValidations';
import PostResponse from 'data/PostResponse';
import Alert from 'features/shared/alerts/components/Alert';
import PromiseLoadingSpinner from 'features/shared/components/loaders/PromiseLoadingSpinner';
import { Accounts_InsertAccountDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import { useTranslation } from 'react-i18next';

interface Props {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  customerId: string;
  contracts: ContractDropdownDTO[];
  contractualAccounts: ContractualAccountDTO[];
  onSubmit: (account: AccountFullDTO) => Promise<PostResponse<string>>;
}

const InsertAccountDialog = ({ show, setShow, customerId, contracts, contractualAccounts, onSubmit }: Props) => {
  // translations
  const tLabels = useTranslation("accounts\\accounts").t;

  // state
  const [addedAccountRestrictions, setAddedAccountRestrictions] = useState<AccountRestrictionDTO[]>([]);
  const [postError, setPostError] = useState<Nullable<PostResponse<string>>>(null);

  // form hook
  const { values, errors, bindField, setFormValue, clearForm, valideteForm } = useForm({
    initialValidations: insertAccountValidations()
  });

  // side-effects
  useEffect(() => {
    if (!show) {
      // after close dialog clear data
      clearForm();
      setAddedAccountRestrictions([]);
      setPostError(null);
    }
  }, [show])

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (valideteForm()) {
      let newAccount: AccountFullDTO = {...values};
      newAccount.customerId = customerId;

      if (newAccount.hasRestrictions && addedAccountRestrictions.length > 0) {
        newAccount.restrictions = addedAccountRestrictions;      
      } else {
        newAccount.hasRestrictions = false;
      }

      let response = await onSubmit(newAccount);

      if (response.status < 300) {
        setShow(false);
      } else {
        setPostError(response);
      }
    }
  };

  const closeDialogHandler = () => {
    setShow(false);
  };

  const roleChangeHandler = (roles: Nullable<AccountRoleDTO[]>) => {
    setFormValue("roles", roles);
  }
  
  // sub-renders
  const renderAccountRestrictionsSubForm = () => {
    if (values["hasRestrictions"] === true) {
      return (
        <AccountRestrictionsSubForm 
          contracts={contracts}
          contractualAccounts={contractualAccounts}
          addedAccountRestrictions={addedAccountRestrictions}
          setAddedAccountRestrictions={setAddedAccountRestrictions}
        />
      );
    } else {
      return null;
    }
  }

  const renderError = () => {
    if (postError) {
      const message = postError.data ?? tLabels("insertAccountDialog.message");

      return (
        <Alert alertObj={{alertType: "Danger", message}} className="mt-4 mb-0 mx-3" />
      );
    }
  }

  // render
  return (
    <Modal show={show} onHide={closeDialogHandler} size="lg" backdrop="static">
      <ModalHeader
        title={tLabels("insertAccountDialog.cardTitle")}
        onCloseClick={closeDialogHandler}
      />
      {renderError()}
      <form onSubmit={submitFormHandler}>
        <div className="modal-body py-4">
          <div className="row no-gutters mx-n2">
            <div className="col-sm-6 px-2">
              <FormGroup
                labelText={tLabels("insertAccountDialog.email")}
                validationError={errors["email"] ?? undefined}
              >
                <input
                  className="form-control"
                  placeholder={tLabels("insertAccountDialog.setUserEmail")}
                  {...bindField("email")}
                />
              </FormGroup>
            </div>
            <div className="col-sm-6 px-2">
              <AccountRoleSelect 
                name="roles"
                onChangeMulti={roleChangeHandler}
                isMulti
                errors={errors}
              />
            </div>
          </div>
          <div className="custom-control custom-checkbox">
            <label>
              <input 
                id="accountDialog-hasRestrictions"
                type="checkbox" 
                className="custom-control-input"
                {...bindField('hasRestrictions', true)} 
              />
              <label className="custom-control-label" htmlFor="accountDialog-hasRestrictions">
                {tLabels("insertAccountDialog.applyRestrictions")}
              </label>
            </label>
          </div>
          {renderAccountRestrictionsSubForm()}
        </div>
        <ModalFooter>
          <button type="submit" className="btn btn-primary">
            {tLabels("insertAccountDialog.addAccount")}
          </button>
        </ModalFooter>
      </form>
      <PromiseLoadingSpinner area={Accounts_InsertAccountDialog} />
    </Modal>
  );
}

export default InsertAccountDialog;
